import { memo, useCallback, useMemo, type ChangeEvent, type FC } from "react";
import dayjs, { type Dayjs } from "dayjs";
import { CalendarDays, ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { WhatsAppMappingType } from "src/services/WhatsAppCampaignAPI";
import StyledDropdown from "src/components/reusable/StyledDropdown";
import StyledTextInput from "src/components/reusable/StyledTextInput";
import { DashboardAntDatePicker } from "src/components/reusable/AntdDatePicker/DashboardAntDatePicker";
import {
  DATETIME_PARAMETER_FORMAT,
  MESSAGE_TIME_FORMAT,
  VARIABLE_MAPPING_TYPES,
  WHATSAPP_EDITOR_KEY,
} from "../WhatsAppCampaignConstants";
import type { WhatsAppVariableDraft } from "../WhatsAppCampaignTypes";
import { createEmptyDraft, formatDraftDate, getVariablePlaceholder } from "../WhatsAppCampaignUtils";

const MAPPING_KEY = `${WHATSAPP_EDITOR_KEY}.mapping`;

const isPastDate = (current: Dayjs) => current.isBefore(dayjs().startOf("day"));

interface ParameterFieldProps {
  variableKey: string;
  draft?: WhatsAppVariableDraft;
  columns: string[];
  onChange: (_key: string, _draft: Partial<WhatsAppVariableDraft>) => void;
}

const ParameterField: FC<ParameterFieldProps> = ({
  variableKey,
  draft = createEmptyDraft(),
  columns,
  onChange,
}) => {
  const { t } = useTranslation();
  const { type, value, fallback } = draft;
  const fieldId = `wa-parameter-${variableKey}`;
  const scope = variableKey.startsWith("header_") ? "header" : "body";

  const typeOptions = useMemo(
    () =>
      VARIABLE_MAPPING_TYPES.map(option => ({
        value: option,
        label: t(`${MAPPING_KEY}.types.${option}`),
      })),
    [t]
  );
  const columnOptions = useMemo(
    () => columns.map(column => ({ value: column, label: column })),
    [columns]
  );
  const dateValue = useMemo(() => (value ? dayjs(value) : null), [value]);

  const handleTypeChange = useCallback(
    (nextType: WhatsAppMappingType) => onChange(variableKey, { type: nextType, value: "", fallback: "" }),
    [onChange, variableKey]
  );
  const handleValueChange = useCallback(
    (nextValue: string) => onChange(variableKey, { value: nextValue }),
    [onChange, variableKey]
  );
  const handleInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => handleValueChange(event.target.value),
    [handleValueChange]
  );
  const handleFallbackChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => onChange(variableKey, { fallback: event.target.value }),
    [onChange, variableKey]
  );
  const handleDateChange = useCallback(
    (date: Dayjs | null) => handleValueChange(date ? formatDraftDate(date) : ""),
    [handleValueChange]
  );

  const renderControl = () => {
    switch (type) {
      case "column":
        return (
          <>
            <StyledDropdown
              id={fieldId}
              value={value || undefined}
              options={columnOptions}
              onChange={handleValueChange}
              placeholder={t(`${MAPPING_KEY}.selectAttribute`)}
              suffixIcon={<ChevronDown size={16} aria-hidden />}
              className="campaign-flow__select"
              popupClassName="campaign-flow__popup"
            />
            <StyledTextInput
              value={fallback}
              onChange={handleFallbackChange}
              placeholder={t(`${MAPPING_KEY}.fallbackPlaceholder`)}
              aria-label={t(`${MAPPING_KEY}.fallbackLabel`)}
              className="campaign-flow__input wa-parameter__fallback"
            />
          </>
        );
      case "date":
        return (
          <DashboardAntDatePicker
            id={fieldId}
            value={dateValue}
            onChange={handleDateChange}
            showTime={{ use12Hours: true, format: MESSAGE_TIME_FORMAT }}
            format={DATETIME_PARAMETER_FORMAT}
            disabledDate={isPastDate}
            placeholder={t(`${MAPPING_KEY}.selectDateTime`)}
            suffixIcon={<CalendarDays size={16} aria-hidden />}
            popupClassName="campaign-flow__popup"
            className="wa-parameter__picker"
          />
        );
      default:
        return (
          <StyledTextInput
            id={fieldId}
            value={value}
            onChange={handleInputChange}
            placeholder={t(`${MAPPING_KEY}.enterValue`)}
            className="campaign-flow__input"
          />
        );
    }
  };

  return (
    <div className="wa-parameter">
      <div className="wa-parameter__header">
        <label htmlFor={fieldId} className="wa-parameter__label">
          <span className="wa-parameter__token">{getVariablePlaceholder(variableKey)}</span>
          {t(`${MAPPING_KEY}.scope.${scope}`)}
          <span className="campaign-flow-field__required" aria-hidden>
            *
          </span>
        </label>
        <StyledDropdown
          value={type}
          options={typeOptions}
          onChange={handleTypeChange}
          aria-label={t(`${MAPPING_KEY}.typeLabel`)}
          className="campaign-flow__select wa-parameter__type"
          popupClassName="campaign-flow__popup"
        />
      </div>
      {renderControl()}
    </div>
  );
};

export default memo(ParameterField);
